import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { BatteryPluggedMode, IPolicyFormStatus, IPolicyInformation, NetworkConfiguration, IApps, IPolicyScreenState, App } from "./types";
import type { androidmanagement_v1 } from '@googleapis/androidmanagement';

const initialStateValues = {
  // policyScreen configuration/navigation
  policyScreen: {
    activeMainScreen: 'devices' as 'devices' | 'policies' | 'accounts' | 'enterprise',
    activeMDMScreen: 'general' as 'general' | 'network' | 'apps' | 'screen' | 'security',
  } as IPolicyScreenState,
  // policyFormStatus this one is only for UI status
  policyFormStatus: {} as IPolicyFormStatus,
  // policyInformation have values outside Android Policy Schema (JSON)
  policyInformation: {} as IPolicyInformation,
  alreadyPolicyInformation: {} as IPolicyInformation,
  // policyJSON have only schemaPolicy from Android Policy Schema
  policyJSON: {} as androidmanagement_v1.Schema$Policy,
  alreadySavedJSON: {} as androidmanagement_v1.Schema$Policy,
  apps: { appsToShow: [] } as IApps,
}
const mdmPolicyStateSlice = createSlice({
  name: "mdmPolicy/state",
  initialState: initialStateValues,
  reducers: {
    setPolicyInformation: (state, action) => {
      state.policyInformation = {
        ...state.policyInformation,
        ...action.payload,
      };
    },
    setAlreadyPolicyInformation: (state, action) => {
      state.alreadyPolicyInformation = {
        ...state.alreadyPolicyInformation,
        ...action.payload,
      };
    },
    resetPolicyJsonState: (state) => {
      state.policyJSON = initialStateValues.policyJSON as androidmanagement_v1.Schema$Policy;
      state.alreadySavedJSON = initialStateValues.alreadySavedJSON as androidmanagement_v1.Schema$Policy;
      state.policyInformation = initialStateValues.policyInformation as IPolicyInformation
      state.alreadyPolicyInformation = initialStateValues.alreadyPolicyInformation as IPolicyInformation
      state.policyFormStatus = initialStateValues.policyFormStatus as IPolicyFormStatus;
    },
    resetMDMPolicyState: (state) => {
      state.policyJSON = initialStateValues.policyJSON as androidmanagement_v1.Schema$Policy;
      state.alreadySavedJSON = initialStateValues.alreadySavedJSON as androidmanagement_v1.Schema$Policy;
      state.policyInformation = initialStateValues.policyInformation as IPolicyInformation
      state.alreadyPolicyInformation = initialStateValues.alreadyPolicyInformation as IPolicyInformation
      state.policyFormStatus = initialStateValues.policyFormStatus as IPolicyFormStatus;
      state.policyScreen = initialStateValues.policyScreen as IPolicyScreenState;
    },
    setPolicyJSON: (state, action: PayloadAction<androidmanagement_v1.Schema$Policy>) => {
      state.policyJSON = {
        ...state.policyJSON,
        ...action.payload,
      };
    },
    setAlreadySavedJSON: (state, action: PayloadAction<androidmanagement_v1.Schema$Policy>) => {
      state.alreadySavedJSON = {
        ...action.payload,
      };
    },
    addPolicyJSONProperties: (state, action: PayloadAction<Partial<androidmanagement_v1.Schema$Policy>>) => {
      state.policyJSON = {
        ...state.policyJSON,
        ...action.payload,
      };
    },
    removePolicyJSONProperty: (state, action: PayloadAction<keyof androidmanagement_v1.Schema$Policy>) => {
      const propertyToRemove = action.payload;
      if (propertyToRemove in state.policyJSON) {
        delete state.policyJSON[propertyToRemove];
      }
    },
    removePropertyOnPluggedModes: (state, action: PayloadAction<BatteryPluggedMode>) => {
      const valueToRemove = action.payload;
      state.policyJSON.stayOnPluggedModes = state.policyJSON.stayOnPluggedModes?.filter(
        (mode) => mode !== valueToRemove
      );
    },
    addPropertyOnPluggedModes: (state, action: PayloadAction<BatteryPluggedMode>) => {
      const valueToAdd = action.payload;
      state.policyJSON.stayOnPluggedModes = [
        ...(state.policyJSON.stayOnPluggedModes ?? []),
        valueToAdd
      ];
    },
    setApps: (state, action: PayloadAction<{ appsToShow: App[] }>) => {
      const newAppsToShow: App[] = action.payload.appsToShow;

      // Merge newAppsToShow with existing appsToShow based on unique identifier
      const mergedApps: App[] = [];
      const existingAppsMap: { [key: string]: App } = {};

      for (const existingApp of state.apps.appsToShow) {
        existingAppsMap[existingApp.AndroidIdentifier] = existingApp;
      }

      for (const newApp of newAppsToShow) {
        if (existingAppsMap[newApp.AndroidIdentifier]) {
          // Update existing app with new properties
          mergedApps.push({
            ...existingAppsMap[newApp.AndroidIdentifier],
            ...newApp,
          });
        } else {
          // Add new app to mergedApps
          mergedApps.push(newApp);
        }
      }

      state.apps.appsToShow = mergedApps;
    },
    setPolicyFormStatus: (state, action: PayloadAction<IPolicyFormStatus>) => {
      state.policyFormStatus = action.payload
    },
    updatePolicyFormStatus: (state, action: PayloadAction<IPolicyFormStatus>) => {
      state.policyFormStatus = { ...state.policyFormStatus, ...action.payload }
    },
    addPolicyInformationProperties: (state, action: PayloadAction<Partial<IPolicyInformation>>) => {
      state.policyInformation = {
        ...state.policyInformation,
        ...action.payload,
      };
    },
    removePolicyInformationProperty: (state, action: PayloadAction<keyof IPolicyInformation>) => {
      const propertyToRemove = action.payload;
      if (propertyToRemove in state.policyInformation) {
        delete state.policyInformation[propertyToRemove];
      }
    },
    setNetworkConfigurationsInPolicyJSON: (state, action: PayloadAction<NetworkConfiguration[]>) => {
      const newNetworkConfigurations = action.payload.filter(
        (newConfig: { GUID: any; }) =>
          !state.policyJSON?.openNetworkConfiguration?.NetworkConfigurations?.some(
            (existingConfig: { GUID: any; }) => existingConfig.GUID === newConfig.GUID
          )
      );

      state.policyJSON = {
        ...state.policyJSON,
        openNetworkConfiguration: {
          NetworkConfigurations: [
            ...(state.policyJSON.openNetworkConfiguration?.NetworkConfigurations ?? []),
            ...newNetworkConfigurations,
          ],
        },
      };
    },
    removeNetworkByGUID: (state, action: PayloadAction<string>) => {
      const guidToRemove = action.payload;
      if (state.policyJSON.openNetworkConfiguration) {
        state.policyJSON.openNetworkConfiguration.NetworkConfigurations = state.policyJSON.openNetworkConfiguration.NetworkConfigurations.filter(
          (config: { GUID: string; }) => config.GUID !== guidToRemove
        );
      }
    },
    setPolicyScreen: (state, action: PayloadAction<IPolicyScreenState>) => {
      state.policyScreen = {
        ...state.policyScreen,
        ...action.payload
      }
    }
  },
});

export const { setPolicyInformation,
  setPolicyJSON,
  addPolicyJSONProperties,
  removePolicyJSONProperty,
  removePropertyOnPluggedModes,
  addPropertyOnPluggedModes,
  resetMDMPolicyState,
  setPolicyFormStatus,
  updatePolicyFormStatus,
  setApps,
  addPolicyInformationProperties,
  removePolicyInformationProperty,
  setNetworkConfigurationsInPolicyJSON,
  removeNetworkByGUID,
  setPolicyScreen, setAlreadySavedJSON,
  setAlreadyPolicyInformation,
  resetPolicyJsonState
} =
  mdmPolicyStateSlice.actions;

export default mdmPolicyStateSlice.reducer;
